import { gql } from '@apollo/client';
import { BlogInfoFragment } from '../../fragments/GeneralSettings';

const BLOG_POST_POLYLANG_FIELDS = `
  language {
    code
  }
  translations {
    language {
      code
    }
    uri
    slug
  }
`;

const BLOG_POST_FIELDS_MINIMAL = `
  databaseId
  title
  content
  excerpt
  slug
  uri
  date
  modified
  featuredImage {
    node {
      sourceUrl
    }
  }
  author {
    node {
      name
    }
  }
  categories {
    nodes {
      slug
      name
    }
  }
`;

/**
 * blogPostEn with authorShown, hero, CTA only (no relatedPostsOverride).
 * Use when full query fails because relatedPostsOverride is not in schema.
 */
const BLOG_POST_FIELDS_MEDIUM = `
  databaseId
  title
  content
  excerpt
  slug
  uri
  date
  modified
  featuredImage {
    node {
      sourceUrl
    }
  }
  author {
    node {
      name
    }
  }
  categories {
    nodes {
      slug
      name
    }
  }
  seoFields {
    seoTitle
    seoDescription
  }
  blogPostEn {
    authorShown
    heroImage {
      node {
        sourceUrl
      }
    }
    ctaTitle
    ctaDescription
    ctaButtonText
    ctaButtonUrl
  }
`;

/**
 * Same as MEDIUM but includes relatedPostsOverride (ACF relationship).
 * If relatedPostsOverride is not in schema, this query fails; then we use MEDIUM so authorShown still works.
 * Includes Polylang language/translations when WPGraphQL Polylang is active (for language switcher).
 */
const BLOG_POST_FIELDS = `
  databaseId
  title
  content
  excerpt
  slug
  uri
  date
  modified
  featuredImage {
    node {
      sourceUrl
    }
  }
  author {
    node {
      name
    }
  }
  categories {
    nodes {
      slug
      name
    }
  }
  seoFields {
    seoTitle
    seoDescription
  }
  ${BLOG_POST_POLYLANG_FIELDS}
  blogPostEn {
    authorShown
    heroImage {
      node {
        sourceUrl
      }
    }
    ctaTitle
    ctaDescription
    ctaButtonText
    ctaButtonUrl
    relatedPostsOverride {
      ... on Post {
        databaseId
        title
        slug
        excerpt
        featuredImage {
          node {
            sourceUrl
          }
        }
        categories {
          nodes {
            name
          }
        }
      }
    }
  }
`;

/** Single blog post by slug from CPT "blog" (try first after migration). */
export const GET_BLOG_BY_SLUG = gql`
  ${BlogInfoFragment}
  query GetBlogBySlug($slug: String!) {
    blogs(where: { name: $slug }, first: 1) {
      nodes {
        ${BLOG_POST_FIELDS}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

export const GET_BLOG_BY_SLUG_MEDIUM = gql`
  ${BlogInfoFragment}
  query GetBlogBySlugMedium($slug: String!) {
    blogs(where: { name: $slug }, first: 1) {
      nodes {
        ${BLOG_POST_FIELDS_MEDIUM}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

export const GET_BLOG_BY_SLUG_MINIMAL = gql`
  ${BlogInfoFragment}
  query GetBlogBySlugMinimal($slug: String!) {
    blogs(where: { name: $slug }, first: 1) {
      nodes {
        ${BLOG_POST_FIELDS_MINIMAL}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

export const GET_BLOG_POST_BY_SLUG = gql`
  ${BlogInfoFragment}
  query GetBlogPostBySlug($slug: String!) {
    posts(where: { name: $slug }, first: 1) {
      nodes {
        ${BLOG_POST_FIELDS}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

/** Used when full query fails (e.g. relatedPostsOverride not in schema). Has blogPostEn so authorShown works. */
export const GET_BLOG_POST_BY_SLUG_MEDIUM = gql`
  ${BlogInfoFragment}
  query GetBlogPostBySlugMedium($slug: String!) {
    posts(where: { name: $slug }, first: 1) {
      nodes {
        ${BLOG_POST_FIELDS_MEDIUM}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

/** Used when MEDIUM also fails (e.g. blogPostEn not in schema). Page still loads, no ACF. */
export const GET_BLOG_POST_BY_SLUG_MINIMAL = gql`
  ${BlogInfoFragment}
  query GetBlogPostBySlugMinimal($slug: String!) {
    posts(where: { name: $slug }, first: 1) {
      nodes {
        ${BLOG_POST_FIELDS_MINIMAL}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

export const GET_BLOG_POST_BY_URI = gql`
  ${BlogInfoFragment}
  query GetBlogPostByUri($uri: String!) {
    nodeByUri(uri: $uri) {
      __typename
      ... on Post {
        ${BLOG_POST_FIELDS}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

export const GET_BLOG_POST_BY_URI_MEDIUM = gql`
  ${BlogInfoFragment}
  query GetBlogPostByUriMedium($uri: String!) {
    nodeByUri(uri: $uri) {
      __typename
      ... on Post {
        ${BLOG_POST_FIELDS_MEDIUM}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

export const GET_BLOG_POST_BY_URI_MINIMAL = gql`
  ${BlogInfoFragment}
  query GetBlogPostByUriMinimal($uri: String!) {
    nodeByUri(uri: $uri) {
      __typename
      ... on Post {
        ${BLOG_POST_FIELDS_MINIMAL}
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

/**
 * Fragment for recent blog posts (front page): title, excerpt, slug, image, author.
 */
export const BLOG_POST_PREVIEW_FIELDS = gql`
  fragment BlogPostPreviewFields on Post {
    slug
    title
    excerpt
    featuredImage {
      node {
        sourceUrl
      }
    }
    author {
      node {
        name
      }
    }
  }
`;

/**
 * Fetches 3 most recent English blog posts for the front page.
 */
export const GET_RECENT_EN_BLOG_POSTS = gql`
  ${BlogInfoFragment}
  ${BLOG_POST_PREVIEW_FIELDS}
  query GetRecentEnBlogPosts {
    generalSettings {
      ...BlogInfoFragment
    }
    recentEnPosts: posts(first: 3) {
      nodes {
        ...BlogPostPreviewFields
      }
    }
  }
`;

/** Fields for a related post card (slug, title, excerpt, image, category). */
const RELATED_POST_FIELDS = `
  databaseId
  title
  slug
  excerpt
  featuredImage {
    node {
      sourceUrl
    }
  }
  categories {
    nodes {
      name
    }
  }
`;

/**
 * Fetches up to 3 related blog posts from CPT "blog" by category (try first after migration).
 */
export const GET_RELATED_BLOGS = gql`
  query GetRelatedBlogs($categorySlug: String!, $excludeId: Int!) {
    blogs(
      where: {
        categoryName: $categorySlug
        notIn: [$excludeId]
      }
      first: 3
    ) {
      nodes {
        ${RELATED_POST_FIELDS}
      }
    }
  }
`;

/**
 * Fetches up to 3 related posts by category, excluding the current post (legacy).
 * Used when ACF relatedPostsOverride is not set.
 */
export const GET_RELATED_POSTS = gql`
  query GetRelatedPosts($categorySlug: String!, $excludeId: Int!) {
    posts(
      where: {
        categoryName: $categorySlug
        notIn: [$excludeId]
      }
      first: 3
    ) {
      nodes {
        ${RELATED_POST_FIELDS}
      }
    }
  }
`;

/** Fields for blog list cards (slug, title, excerpt, image, author, categories). */
const BLOG_LIST_FIELDS = `
  databaseId
  title
  slug
  excerpt
  featuredImage {
    node {
      sourceUrl
    }
  }
  author {
    node {
      name
    }
  }
  categories {
    nodes {
      slug
      name
    }
  }
`;

/** Same as BLOG_LIST_FIELDS but includes ACF authorShown (blogPostEn). */
const BLOG_LIST_FIELDS_WITH_ACF = `
  databaseId
  title
  slug
  excerpt
  featuredImage {
    node {
      sourceUrl
    }
  }
  author {
    node {
      name
    }
  }
  categories {
    nodes {
      slug
      name
    }
  }
  blogPostEn {
    authorShown
    heroImage {
      node {
        sourceUrl
      }
    }
  }
`;

/**
 * Fetches blog list from CPT "blog" (after migration from posts + category blog/blogi).
 * Tried first; falls back to GET_BLOG_POSTS if schema has no blogs.
 */
export const GET_BLOGS = gql`
  ${BlogInfoFragment}
  query GetBlogs($categorySlug: String!, $first: Int) {
    generalSettings {
      ...BlogInfoFragment
    }
    blogs(where: { categoryName: $categorySlug }, first: $first) {
      nodes {
        ${BLOG_LIST_FIELDS}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_BLOGS_WITH_ACF = gql`
  ${BlogInfoFragment}
  query GetBlogsWithAcf($categorySlug: String!, $first: Int) {
    generalSettings {
      ...BlogInfoFragment
    }
    blogs(where: { categoryName: $categorySlug }, first: $first) {
      nodes {
        ${BLOG_LIST_FIELDS_WITH_ACF}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/**
 * Fetches posts for the blog list page (legacy).
 * @param categorySlug - Optional. When set, filters by this category slug. Omit for all posts.
 * @param lang - 'en' or 'fi'. Used to filter by language category (blog vs blogi) if WP uses separate parent categories.
 */
export const GET_BLOG_POSTS = gql`
  ${BlogInfoFragment}
  query GetBlogPosts($categorySlug: String!, $first: Int) {
    generalSettings {
      ...BlogInfoFragment
    }
    posts(where: { categoryName: $categorySlug }, first: $first) {
      nodes {
        ${BLOG_LIST_FIELDS}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/** Same as GET_BLOG_POSTS but includes ACF authorShown. Use when blogPostEn is in schema. */
export const GET_BLOG_POSTS_WITH_ACF = gql`
  ${BlogInfoFragment}
  query GetBlogPostsWithAcf($categorySlug: String!, $first: Int) {
    generalSettings {
      ...BlogInfoFragment
    }
    posts(where: { categoryName: $categorySlug }, first: $first) {
      nodes {
        ${BLOG_LIST_FIELDS_WITH_ACF}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/**
 * Blog list with Polylang language filter (WPGraphQL Polylang).
 * Use for EN blog when Polylang adds language to posts and category alone returns no/few posts.
 * Enum is typically LanguageCodeEnum: EN, FI (or en, fi depending on plugin).
 */
export const GET_BLOG_POSTS_WITH_ACF_BY_LANGUAGE = gql`
  ${BlogInfoFragment}
  query GetBlogPostsWithAcfByLanguage($categorySlug: String!, $first: Int, $language: LanguageCodeEnum) {
    generalSettings {
      ...BlogInfoFragment
    }
    posts(where: { categoryName: $categorySlug, language: $language }, first: $first) {
      nodes {
        ${BLOG_LIST_FIELDS_WITH_ACF}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/** Same as above without ACF. Fallback when WITH_ACF_BY_LANGUAGE fails (e.g. no ACF). */
export const GET_BLOG_POSTS_BY_LANGUAGE = gql`
  ${BlogInfoFragment}
  query GetBlogPostsByLanguage($categorySlug: String!, $first: Int, $language: LanguageCodeEnum) {
    generalSettings {
      ...BlogInfoFragment
    }
    posts(where: { categoryName: $categorySlug, language: $language }, first: $first) {
      nodes {
        ${BLOG_LIST_FIELDS}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
